import { formatAxiosError } from "@/_services/axios"
import consoleService from "@/_services/console"
import { getListsValues } from "@/_services/lists"
import { loc } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
// import { isLocalhost } from "@/_services/serviceWorker"
import { getIsAdmin, getOptions, getUsername } from "@/_services/userConfiguration"
import { getAppVersion, localStorageKeys, personRoles } from "@/_services/utils"
import axios from "axios"
import { convertDateStringsToDates, toQueryString } from "basikon-common-utils"
import sassVars from "../_styles/sass-vars.module.scss"

const cacheName = "hyperfront-sw-cache"

// these include parameters must be kept in sync
// so that searching in the service worker cache the keys match
export const contractsIncludes = "partnerName,salesName,assetsNames"
export const contractIncludes = "assets,persons,fundings,documents,collaterals,financingServices"
export const prospectIncludes = "descendantsNames,documents"
export const scoreIncludes = "documents"

const state = {
  offlineStateUpdateFn: null,
  offlineSince: null,
  counter: null,
  history: null,
}

function registerOfflineStateUpdateFn(offlineStateUpdateFn, history) {
  state.offlineStateUpdateFn = offlineStateUpdateFn
  state.history = history
}

function isActivated() {
  return window.location.origin.startsWith("https://") && getOptions("offlineSupport") // || isLocalhost
}

function setOffline(_isOffline, force) {
  if (!force && !isActivated()) return // do nothing
  if (consoleService.showOfflineLog) console.log("setOffline()", _isOffline)
  if (_isOffline) {
    if (state.offlineSince) return // already offline
    state.offlineSince = new Date()
    if (typeof state.offlineStateUpdateFn === "function") state.offlineStateUpdateFn(state.offlineSince)
    addNotification("You are now offline and using cached data", "warning", "tc")
    if (!force) offlinePing()
  } else {
    if (!state.offlineSince) return // already online
    state.offlineSince = null
    if (typeof state.offlineStateUpdateFn === "function") state.offlineStateUpdateFn()
    offlineExecuteActions(true)
  }
}

function isOffline() {
  return state.offlineSince ? true : false
}

async function offlinePing() {
  if (isOffline()) {
    try {
      await axios.get("/api/core/ping")
      setOffline(false)
    } catch {} // eslint-disable-line
    setTimeout(offlinePing, 2000)
  }
}

function rewriteTmpRegistrationDeep(value, tmpRegistration, savedRegistration) {
  if (Array.isArray(value)) {
    value.forEach(item => rewriteTmpRegistrationDeep(item, tmpRegistration, savedRegistration))
  } else if (value && typeof value === "object") {
    for (const key of Object.keys(value)) {
      if (value[key] === tmpRegistration) {
        value[key] = savedRegistration
      } else {
        rewriteTmpRegistrationDeep(value[key], tmpRegistration, savedRegistration)
      }
    }
  }
}

function getNextRegistration() {
  state.counter = (state.counter || 0) + 1
  let registration = state.counter.toString()
  while (registration.length < 8) registration = "0" + registration
  console.log("---- offlineService: getNextRegistration() returns", "TMP" + registration)
  return "TMP" + registration
}

function offlineActionSortRank(action) {
  if (action.offlineActionType === "transition") return 3
  if (action.method === "patch") return 2
  if (action.key.startsWith("/api/financing/contracts")) return 1
  if (action.key.startsWith("/api/person/prospects") && isClientLeadProspect(action.data)) return 0.5
  return 0
}

function isClientLeadProspect(data) {
  const roles = data?.roles
  if (Array.isArray(roles)) return roles.includes("CLIENT")
  if (typeof roles === "string") return roles.split(",").includes("CLIENT")
  return false
}

function offlinePush(action) {
  const offlineActions = readLocalStorage()
  action.username = getUsername()

  switch (action.method) {
    // post is for contractPage, assets, persons, prospects, scores
    case "post": {
      if (action.offlineActionType === "transition") {
        action.key = `${action.url}#${action.data?.code || ""}`

        const index = offlineActions.findIndex(it => it.key === action.key)
        if (index >= 0) {
          offlineActions[index] = action
        } else {
          offlineActions.push(action)
        }

        break
      }

      // set tmp registration if none
      let registration = action.data.registration || action.data._id
      if (!registration) {
        registration = getNextRegistration()
        action.data.registration = registration
        action.data._id = registration
      }

      const [urlPath, urlQuery] = action.url.split("?")
      action.key = urlPath + "/" + registration + (urlQuery ? "?" + urlQuery : "")

      // keep only one action with same key (or _update will be wrong the second time)
      const index = offlineActions.findIndex(it => it.key === action.key)
      if (index >= 0) {
        offlineActions[index] = action
      } else {
        offlineActions.push(action)
      }

      // keep some flag for debug purpose
      //action.data._fromOfflineCache = true

      break
    }
    case "patch": {
      // patch is for tasks
      action.key = action.url
      offlineActions.push(action)

      break
    }
    default: {
      throw Error("offlineService: offlinePush(): invalid method", action.method)
    }
  }

  // sort actions to get contract at the end, so that tmp registration get updated if needed
  // this is required in special case when user saves a contract a first time while offline, then adds a new asset.
  // In that case this new asset would be after the contract that references it
  offlineActions.sort((a, b) => offlineActionSortRank(a) - offlineActionSortRank(b))

  writeLocalStorage(offlineActions)
  return { ...action.data }
}

async function offlineExecuteActions(messageFromWorker) {
  if (consoleService.showOfflineLog) console.log("offlineService: execute()")
  if (!isActivated()) return // do nothing

  let offlineActions = readLocalStorage()

  // remove any action with wrong username (just in case user switched at some point)
  offlineActions = offlineActions.filter(it => it.username === getUsername())

  const offlineActionsErrors = []
  const nbActions = offlineActions.length
  if (nbActions === 0) {
    if (messageFromWorker) addNotification("You are now online", "success", "tc")
  } else {
    const allConvertedTmpRegistration = []
    while (offlineActions.length > 0) {
      const offlineAction = offlineActions[0]
      console.log("---- offlineExecuteActions execute", offlineAction)

      const tmpRegistration = offlineAction.data.registration?.startsWith("TMP0000") ? offlineAction.data.registration : null
      if (tmpRegistration) {
        offlineAction.data.tmpRegistration = tmpRegistration
        delete offlineAction.data.registration
        delete offlineAction.data._id
      }

      let savedData
      try {
        savedData = (await axios(offlineAction)).data
      } catch (error) {
        if (error.response) {
          // server was reached but returned an error (probably out of date, or bug)
          offlineActions[0].error = error
          const offlineActionCopy = { ...offlineActions[0], error }
          offlineActionsErrors.push(offlineActionCopy)
          console.log("offlineExecuteActions() error", offlineActionCopy)
        } else {
          // server could not be reached (probably a network error caused by poor network)
          console.log("offlineExecuteActions() error", error)
          offlineActions[0].trials = (offlineActions[0].trials || 0) + 1
          addOops(error)
          setOffline(true)
          break
        }
      }

      const transitionErrors = offlineAction.offlineActionType === "transition" ? savedData?.transitionResult?.errors : null
      if (Array.isArray(transitionErrors) && transitionErrors.length > 0) {
        const error = { response: { data: { errors: transitionErrors } } }
        offlineActions[0].error = error
        offlineActionsErrors.push({ ...offlineActions[0], error })
        console.log("offlineExecuteActions() transition rejected", transitionErrors)
      }

      // patch following "foreign keys" if some registration has been allocated
      let key = offlineAction.key
      const savedRegistration = savedData?.registration
      if (tmpRegistration && savedRegistration) {
        allConvertedTmpRegistration.push({ tmpRegistration, savedRegistration })
        for (const remainingAction of offlineActions) {
          rewriteTmpRegistrationDeep(remainingAction.data, tmpRegistration, savedRegistration)

          if (remainingAction.url?.includes(tmpRegistration)) {
            remainingAction.url = remainingAction.url.split(tmpRegistration).join(savedRegistration)
          }
          if (remainingAction.key?.includes(tmpRegistration)) {
            remainingAction.key = remainingAction.key.split(tmpRegistration).join(savedRegistration)
          }
        }

        // reload key to update workbox cache with newly saved data
        key = key.substring(0, offlineAction.key?.lastIndexOf("/")) + "/" + savedRegistration
      } else if (offlineAction.offlineActionType === "transition") {
        key = offlineAction.url.replace(/\/transitions(\?.*)?$/, "")
      }

      try {
        await axios.get(key)
      } catch (error) {
        if (error.response) {
          offlineActions[0].error = error
          const offlineActionCopy = { ...offlineActions[0], error }
          offlineActionsErrors.push(offlineActionCopy)
          console.log("offlineExecuteActions() error", offlineActionCopy)
        }
      }

      // When there is a synchronization error (apart from being offline) with the server, the offline actions are lost here.
      // An example of such errors is when the entity workflow has checks that prevents saving the entity.
      // In offline mode they can be check when saving locally the entity, but when network connectivity is restored,
      // the temp data is rejected by the server.
      // Should we one day need to avoid that, we would have to store them somewhere.
      offlineActions.shift()
    }

    // notify user
    if (offlineActionsErrors.length === 0) {
      const action = { label: "Got it", callback: () => setTimeout(window.location.reload()) }
      addNotification("You are now online and server has been updated with your changes", "success", "tc", action)
    } else {
      let message = loc`Error occured while synchronizing your data to the server`
      const errorMessages = offlineActionsErrors.map(actionError => {
        const entityType = actionError.key.startsWith("/api/financing/contracts")
          ? loc`Contract`
          : actionError.key.startsWith("/api/asset/assets")
            ? loc`Asset`
            : actionError.key.startsWith("/api/person/prospects")
              ? loc`Prospect`
              : actionError.key.startsWith("/api/person/persons")
                ? loc`Person`
                : actionError.key.startsWith("/api/scoring/scores")
                  ? loc`Score`
                  : actionError.key.startsWith("/api/core/tasks")
                    ? loc`Task`
                    : loc`???`
        return (
          entityType +
          " " +
          (actionError.data.registration || actionError.data.tmpRegistration || actionError.data.entity?.registration) +
          " (" +
          formatAxiosError(actionError.error) +
          ")"
        )
      })
      message += errorMessages.map(it => "\n" + it)
      const action = { label: "Got it", callback: () => setTimeout(window.location.reload()) }
      addNotification(message, "error", "tc", action)

      // save bad records in another key
      let errorStorage = JSON.parse(localStorage.getItem(localStorageKeys.OFFLINE_MODE.STORAGE_ERRORS)) || []
      while (errorStorage.length > 5) errorStorage.splice(errorStorage.length - 1, 1)
      errorStorage = [offlineActions].concat(errorStorage)
      localStorage.setItem(localStorageKeys.OFFLINE_MODE.STORAGE_ERRORS, JSON.stringify(errorStorage))
      offlineActions = []
    }
    writeLocalStorage(offlineActions)

    const { history } = state
    console.log("---- offlineService current history is", history?.location.pathname)
    for (const it of allConvertedTmpRegistration) {
      const { tmpRegistration, savedRegistration } = it
      if (history?.location.pathname.endsWith("/" + tmpRegistration)) {
        const newHistory = history?.location.pathname.replace(tmpRegistration, savedRegistration)
        console.log("---- offlineService change history to", newHistory)
        history.replace(newHistory)
        break
      }
    }
  }
  preload()
}

/**
 * No need to await for most calls after the cache has been opened.
 */
async function preload() {
  if (isOffline()) return
  if (!isActivated()) return // do nothing
  console.log("offlineService: preload()")

  const currentAppVersion = getAppVersion()
  const prevAppVersion = localStorage.getItem(localStorageKeys.OFFLINE_MODE.STORAGE_VERSION)
  let force = false
  if (currentAppVersion !== prevAppVersion) {
    force = true
    localStorage.setItem(localStorageKeys.OFFLINE_MODE.STORAGE_VERSION, currentAppVersion)
  }

  const workboxCache = await window.caches.open(cacheName)

  const offlineSupportOptions = getOptions("offlineSupport")
  const { preloadContractsCount, preloadIcons, preloadProspects, preloadScores, preloadLists, preloadScripts, preloadPage, preloadUri } =
    offlineSupportOptions || {}

  // this makes sure these lists are available when offline
  getListsValues((Array.isArray(preloadLists) ? preloadLists : []).join(","), { storeForOfflineUse: true })

  // load URIs that don't update often only if not yet loaded
  for (const uri of Array.isArray(preloadUri) ? preloadUri : []) {
    if (!(await workboxCache.match(uri))) axios.get(uri).catch(() => {})
  }

  if (preloadScripts?.length) {
    for (const script of preloadScripts) {
      const uri = `/api/script/scripts/${script}`
      if (!(await workboxCache.match(uri))) axios.get(uri).catch(() => {})
    }
  }

  if (Array.isArray(preloadIcons) && preloadIcons?.length) {
    const icns = sassVars.icns.split(",")
    for (let i = 0; i < icns?.length; i++) {
      const icn = icns[i].trim()
      if (preloadIcons.includes(icn)) {
        axios.get(`/icons/${icn}.svg`)
      }
    }
  }

  // this is to avoid duplicate requests, thus accelerating the initial load
  const fetchedUrls = {}

  if (Number.isFinite(preloadContractsCount)) {
    axios.get("/api/person/persons")
    axios.get("/api/core/tasks")

    // preload first contracts and their data, if they have changed
    // the include param must be kept in sync with DealsOrContractsPage
    const contracts = (await axios.get(`/api/financing/contracts${toQueryString({ include: contractsIncludes })}`)).data
    let count = 0
    for (const contract of contracts) {
      if (count++ >= preloadContractsCount) break

      const contractKey = `/api/financing/contracts/${contract.registration}`
      const contractsUrl = `/api/financing/contracts/${contract.registration}${toQueryString({ include: contractIncludes })}`
      const cachedResponse = await workboxCache.match(contractsUrl)
      const cachedContract = cachedResponse ? await cachedResponse.json() : undefined
      const contractIsoDate = contract._updateDate?.toISOString()

      if (!force && cachedContract?._updateDate === contractIsoDate) {
        console.log(`offlineService: preload() contract is up to date: ${contract.registration}`)
        continue
      }

      console.log(
        `offlineService: preload() contract needs preloading: ${contract.registration} ${cachedContract?._updateDate || ""} ${contractIsoDate}`,
      )

      if (!fetchedUrls[contractsUrl]) {
        fetchedUrls[contractsUrl] = true
        axios.get(contractsUrl)
      }

      const contractTransitionsUrl = `${contractKey}/transitions?type=${contract.type}`
      if (!fetchedUrls[contractTransitionsUrl]) {
        fetchedUrls[contractTransitionsUrl] = true
        axios.get(contractTransitionsUrl)
      }

      const contractDocumentsUrl = `${contractKey}/documents`
      if (!fetchedUrls[contractDocumentsUrl]) {
        fetchedUrls[contractDocumentsUrl] = true
        axios.get(contractDocumentsUrl)
      }

      for (const { personRegistration, role } of contract.persons) {
        if (!personRegistration) continue

        const personUrl = `/api/person/persons/${personRegistration}`
        const personTransitionsUrl = `/api/person/persons/${personRegistration}/transitions?type=I`

        if (!fetchedUrls[personUrl]) {
          fetchedUrls[personUrl] = true
          axios.get(personUrl)
        }

        if (!fetchedUrls[personTransitionsUrl]) {
          fetchedUrls[personTransitionsUrl] = true
          axios.get(personTransitionsUrl)
        }

        if (getIsAdmin()) {
          const userOfPersonUrl = `/api/core/users?person=${personRegistration}`
          if (!fetchedUrls[userOfPersonUrl]) {
            fetchedUrls[userOfPersonUrl] = true
            axios.get(userOfPersonUrl)
          }
        }

        if (role === personRoles.PARTNER) {
          const agreementUrl = `/api/financing/agreements/current-for-person/${personRegistration}`
          if (!fetchedUrls[agreementUrl]) {
            fetchedUrls[agreementUrl] = true
            axios.get(agreementUrl)
          }
        }
      }

      for (const { assetRegistration } of contract.assets) {
        if (!assetRegistration) continue

        const assetUrl = `/api/asset/assets/${assetRegistration}`
        if (!fetchedUrls[assetUrl]) {
          fetchedUrls[assetUrl] = true
          axios.get(assetUrl)
        }
      }
    }
  }

  if (preloadProspects?.url) {
    const { url, projection, count: preloadProspectsCount = 30 } = preloadProspects
    try {
      const prospects = (await axios.get(url + (projection ? toQueryString({ projection }) : ""))).data

      let prospectCount = 0
      const { prospectIncludeFields = [] } = preloadProspects
      for (const prospect of prospects) {
        if (prospectCount++ >= preloadProspectsCount) break
        await preloadEntity({
          entity: prospect,
          entityUrl: `/api/person/prospects/${prospect.registration}`,
          include: prospectIncludes,
          fetchedUrls,
          prospectIncludeFields,
        })
      }
    } catch {
      //
    }
  }

  if (preloadScores?.url) {
    const { url, projection, count: preloadScoresCount = 20 } = preloadScores
    try {
      const scores = (await axios.get(url + (projection ? toQueryString({ projection }) : ""))).data

      let scoreCount = 0
      const { prospectIncludeFields = [] } = preloadScores
      for (const score of scores) {
        if (scoreCount++ >= preloadScoresCount) break
        await preloadEntity({
          entity: score,
          entityUrl: `/api/scoring/scores/${score.registration}`,
          include: scoreIncludes,
          fetchedUrls,
          prospectIncludeFields,
        })
      }
    } catch {
      //
    }
  }

  if (preloadProspects?.url) {
    import("@/person/ProspectsOrPersonsPage").catch(() => {})
    import("@/person/ProspectOrPersonPage").catch(() => {})
  }

  if (preloadScores?.url) {
    import("@/score/ScorePage").catch(() => {})
    import("@/_pages/EntitiesPage").catch(() => {})
    import("@/_components/NavsCard").catch(() => {})
  }

  if (preloadPage) {
    import("@/_pages/Page").catch(() => {})
    import("@/_services/scripts").catch(() => {})
    import("@/_components/ContentComponent").catch(() => {})
  }
}

async function preloadEntity({ entity, entityUrl, include, fetchedUrls, prospectIncludeFields }) {
  if (!entity.registration) return

  if (fetchedUrls[entityUrl]) return
  fetchedUrls[entityUrl] = true

  // include param must match ProspectOrPersonPage's getEntity() call, or the cached response
  // won't be found when the page requests the same registration while offline
  try {
    entity = (await axios.get(entityUrl + toQueryString({ include }))).data
  } catch {
    return
  }

  const entityTransitionsUrl = `${entityUrl}/transitions?type=${entity.type}`
  if (!fetchedUrls[entityTransitionsUrl]) {
    fetchedUrls[entityTransitionsUrl] = true
    axios.get(entityTransitionsUrl).catch(() => {})
  }

  for (const prospectIncludeRegistration of prospectIncludeFields.map(field => entity?.[field])) {
    if (!prospectIncludeRegistration) continue
    const prospectIncludeUrl = `/api/person/prospects/${prospectIncludeRegistration}`
    if (!fetchedUrls[prospectIncludeUrl]) {
      fetchedUrls[prospectIncludeUrl] = true
      axios.get(prospectIncludeUrl).catch(() => {})
    }
  }
}

function readLocalStorage() {
  return JSON.parse(localStorage.getItem(localStorageKeys.OFFLINE_MODE.STORAGE_NAME)) || []
}

function writeLocalStorage(actions) {
  return localStorage.setItem(localStorageKeys.OFFLINE_MODE.STORAGE_NAME, JSON.stringify(actions))
}

async function findCachedOfflineTransitions(serverRoute, status) {
  const workboxCache = await window.caches.open(cacheName)
  const requests = await workboxCache.keys()
  const transitionsByCode = {}

  for (const request of requests) {
    if (!request.url.includes(`${serverRoute}/`) || !request.url.includes("/transitions")) continue

    try {
      const response = await workboxCache.match(request)
      const transitions = (await response.json()) || []
      for (const transition of transitions) {
        if (
          transition.offline === true &&
          (!Array.isArray(transition.source) || transition.source.includes(status)) &&
          !transitionsByCode[transition.code]
        ) {
          transitionsByCode[transition.code] = transition
        }
      }
    } catch {
      // ignore malformed or unrelated cache entries
    }
  }

  return Object.values(transitionsByCode)
}

// this API is to store in a secondary cache (e.g. on top of not the workbox cache)
// only entities that can be changed while offline should be saved through this API
async function axiosOrPush(offlineAction) {
  if (isOffline()) {
    return offlinePush(offlineAction)
  } else {
    try {
      return (await axios(offlineAction)).data
    } catch (error) {
      if (isActivated() && !error.response) {
        console.log("==== assume offline", offlineAction)
        setOffline(true)
        return offlinePush(offlineAction)
      } else {
        throw error
      }
    }
  }
}

// this API is not for "general" caching, general caching on GETs is done by workbox
// this API should be used only in coordination with axiosorPush(), only for entities that can be modified when offline
// (e.g. contract, contract persons, assets)
const listApiPathsWithOfflineCreateSupport = ["/api/financing/contracts", "/api/person/prospects", "/api/scoring/scores"]

async function axiosGetWithCacheSupport(url, options) {
  if (isOffline()) {
    const offlineActions = readLocalStorage()

    // we want exact matches here to avoid paths like /api/financing/contracts/:contractId/documents
    const listApiPath = listApiPathsWithOfflineCreateSupport.find(path => url === path || url.startsWith(`${path}?`))

    if (listApiPath) {
      // prepend any TMP entity of this type
      const workboxCache = await window.caches.open(cacheName)
      const cachedResponse = await workboxCache.match(url)
      if (cachedResponse) {
        let cachedEntities = await cachedResponse.json()
        for (const action of offlineActions) {
          if (action.key.startsWith(`${listApiPath}/TMP0000`)) {
            convertDateStringsToDates(action.data)
            cachedEntities = [action.data, ...cachedEntities]
          }
        }
        return { data: cachedEntities, headers: {} }
      }
    } else {
      // return last cached if any
      for (let i = offlineActions.length - 1; i >= 0; i--) {
        if (url.startsWith(offlineActions[i].key) && !offlineActions[i].trials) {
          convertDateStringsToDates(offlineActions[i].data)
          return { data: offlineActions[i].data, headers: {} }
        }
      }
    }
  }

  return axios.get(url, options)
}

export {
  axiosGetWithCacheSupport,
  axiosOrPush,
  findCachedOfflineTransitions,
  isOffline,
  offlineExecuteActions,
  readLocalStorage,
  registerOfflineStateUpdateFn,
  setOffline,
}
